import React, { useState } from 'react';
import { Box, Typography, Button, Grid, Chip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import KpiCard from '../components/KpiCard';
import RepairCostsByCategory from '../components/RepairCostsByCategory';
import AiDrivenInsights from '../components/AiDrivenInsights';
import AssetsAtRisk from '../components/AssetsAtRisk';
import OptimizationGoalsModal from '../components/OptimizationGoalsModal';
import Footer from '../../../shared/components/Footer';
import { getDashboardDataByZone } from '../data/dashboardMockData';
import { getAssetsByZone } from '../../assets/data/assetsMockData';
import { useZone } from '../../../shared/context/ZoneContext';

const DashboardPage = () => {
  const [optimizationOpen, setOptimizationOpen] = useState(false);
  const { selectedZone } = useZone();
  const { kpiData, serviceCostsData, aiInsights } = getDashboardDataByZone(selectedZone);
  const zoneAssets = getAssetsByZone(selectedZone);
  const criticalAssets = zoneAssets.filter((asset) => asset.riskLevel === 'Critical');

  const handleOptimizationSave = (settings) => {
    console.log('Optimization settings saved:', settings);
  };

  return (
    <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3, flexGrow: 1 }}>
        {/* Header Row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesomeIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
            <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
              Overview
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setOptimizationOpen(true)}
              sx={{
                bgcolor: '#399432',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.8125rem',
                px: 2.5,
                py: 0.75,
                borderRadius: 6,
                '&:hover': { bgcolor: '#2E7628' },
              }}
              startIcon={
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: '#fff',
                  }}
                />
              }
            >
              Optimization Goals
            </Button>
            <Chip
              icon={<CalendarTodayOutlinedIcon sx={{ fontSize: '14px !important' }} />}
              label="Last Month (Jan 20 - Feb 20)"
              variant="outlined"
              sx={{
                borderColor: '#E5E7EB',
                color: 'text.primary',
                fontWeight: 500,
                fontSize: '0.8125rem',
                height: 36,
                '& .MuiChip-icon': { color: 'text.secondary' },
              }}
            />
          </Box>
        </Box>

        {/* KPI Cards - Row 1 */}
        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          {kpiData.slice(0, 3).map((kpi) => (
            <Grid size={{ xs: 12, md: 4 }} key={kpi.id}>
              <KpiCard data={kpi} />
            </Grid>
          ))}
        </Grid>

        {/* KPI Cards - Row 2 */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {kpiData.slice(3, 6).map((kpi) => (
            <Grid size={{ xs: 12, md: 4 }} key={kpi.id}>
              <KpiCard data={kpi} />
            </Grid>
          ))}
        </Grid>

        {/* Middle Section: Repair Costs + AI Insights */}
        <Grid container spacing={2.5} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <RepairCostsByCategory data={serviceCostsData} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <AiDrivenInsights insights={aiInsights} />
          </Grid>
        </Grid>

        {/* Assets at Risk */}
        <AssetsAtRisk assets={criticalAssets.slice(0, 4)} />
      </Box>

      {/* Footer */}
      <Footer />

      {/* Optimization Goals Modal */}
      <OptimizationGoalsModal
        open={optimizationOpen}
        onClose={() => setOptimizationOpen(false)}
        onSave={handleOptimizationSave}
      />
    </Box>
  );
};

export default DashboardPage;
